/**
 * Client-side Open Graph / Twitter Card helpers (no react-helmet).
 */

export const SITE_ORIGIN = "https://6amico.com";
export const DEFAULT_SEO_TITLE = "6amico - Il social network dei borghi italiani";
export const DEFAULT_SEO_DESC =
  "Il social network dei borghi italiani: schede di comuni e frazioni, eventi e sagre, prodotti tipici, gemellaggi e la Piazza Social della community.";
export const DEFAULT_SEO_IMAGE = "https://6amico.com/og-default.jpg";

let activePostSeoId = null;

export function upsertMeta(attr, key, content) {
  if (typeof document === "undefined" || !content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Meta description da testo grezzo/markdown: pulisce e taglia a ~155 char
 * al confine di parola/frase.
 */
export function seoDescriptionFrom(text, maxLen = 155) {
  const s = String(text || "")
    .replace(/!\[?[^\]\n]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#*`_>]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  if (s.length <= maxLen) return s;
  const cut = s.slice(0, maxLen);
  const atDot = cut.lastIndexOf(". ");
  if (atDot > maxLen * 0.5) return cut.slice(0, atDot + 1);
  const atSpace = cut.lastIndexOf(" ");
  return `${(atSpace > 60 ? cut.slice(0, atSpace) : cut).trim()}…`;
}

export function upsertCanonical(url) {
  if (typeof document === "undefined" || !url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export function applySeo({
  title,
  description,
  url,
  image = DEFAULT_SEO_IMAGE,
  type = "website",
  locale,
  robots,
}) {
  if (typeof document === "undefined") return;
  const safeImage =
    !image || image === SITE_ORIGIN || image === `${SITE_ORIGIN}/`
      ? DEFAULT_SEO_IMAGE
      : image;

  document.title = title;
  if (url) upsertCanonical(url);
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", safeImage);
  upsertMeta("property", "og:type", type || "website");
  upsertMeta("property", "og:site_name", "6amico");
  if (locale) upsertMeta("property", "og:locale", locale);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", safeImage);
  if (robots) upsertMeta("name", "robots", robots);
}

const STATIC_PAGE_SEO = {
  "/benvenuto": {
    title: "Benvenuto su 6amico - Come funziona la rete dei borghi",
    description:
      "Benvenuto su 6amico: scopri come funziona la rete dei borghi italiani — schede comune, frazioni, eventi, Piazza Social e passaporto digitale.",
  },
  "/come-funziona": {
    title: "Come funziona 6amico - Borghi, eventi e Piazza Social",
    description:
      "Come funziona 6amico: esplora comuni e frazioni, segui eventi e sagre, pubblica nella Piazza Social e raccogli i timbri del passaporto.",
  },
  "/comuni-amici": {
    title: "Comuni amici e relazioni territoriali - 6amico",
    description:
      "I comuni amici su 6amico: gemellaggi istituzionali, legami territoriali e reti tra borghi verificate su fonti ufficiali.",
  },
  "/manifesto": {
    title: "Il manifesto di 6amico - Il social network dei borghi",
    description:
      "Il manifesto di 6amico: dare voce a borghi, frazioni e territori italiani con schede aperte, eventi, memoria storica e community.",
  },
  "/mondo": {
    title: "Gemellaggi e città amiche | 6amico",
    description:
      "Gemellaggi e città amiche su 6amico: le città del mondo legate ai borghi italiani — patti di amicizia, schede e relazioni territoriali.",
  },
  "/mappa-del-mondo": {
    title: "Mappa del mondo - Esplora l'Italia e la rete 6amico",
    description:
      "Scopri l'Italia su 6amico: borghi attivi, mappe territoriali, podcast geolocalizzati, parchi e città del mondo — il layer vivo della community.",
  },
  "/archivio-podcast": {
    title: "Archivio Podcast - le prime voci dei borghi | 6amico",
    description:
      "Le prime voci dei borghi su 6amico: podcast e capsule audio geolocalizzate su monumenti, sapori e storie del territorio.",
  },
  "/eccellenze": {
    title: "Eccellenze dei borghi - sapori e prodotti tipici | 6amico",
    description:
      "Il catalogo delle eccellenze enogastronomiche dei borghi 6amico: schede prodotto, varianti di famiglia e tecniche tramandate.",
  },
  "/gemellaggi": {
    title: "Gemellaggi tra comuni - rete istituzionale | 6amico",
    description:
      "Gemellaggi e patti di amicizia tra comuni italiani e città del mondo su 6amico: dati verificati su fonti ufficiali.",
  },
  "/mappa-mondo": {
    title: "Mappa globale - città e gemellaggi | 6amico",
    description:
      "La mappa globale 6amico: cluster di città internazionali, borghi partner e gemellaggi su una sola vista Leaflet.",
  },
  "/comuni": {
    title: "Borghi attivi - tutti i comuni della rete | 6amico",
    description:
      "I comuni italiani su 6amico: borghi partner con scheda completa e schede territoriali per tutta Italia, filtrabili per regione.",
  },
  "/censisci-luogo": {
    title: "Censisci un luogo - Segnala un posto del territorio | 6amico",
    description:
      "Censisci un luogo su 6amico: segnala un monumento, una piazza o un punto d'interesse del tuo territorio con foto e posizione sulla mappa.",
  },
};

export function resolveLaunchSeo(pathname, search) {
  const params = new URLSearchParams(search || "");
  const canonical = `${SITE_ORIGIN}${pathname}${search || ""}`;

  const staticSeo = STATIC_PAGE_SEO[pathname];
  if (staticSeo) {
    return { ...staticSeo, url: `${SITE_ORIGIN}${pathname}`, image: DEFAULT_SEO_IMAGE };
  }

  if (pathname === "/piazze" || pathname.startsWith("/piazze/")) {
    return {
      title: "Diario e Piazza Social · 6amico",
      description:
        "Il Diario di Bordo e la Piazza Social di 6amico: ricordi, timbri e post dalla community dei borghi italiani.",
      url: canonical,
      image: DEFAULT_SEO_IMAGE,
    };
  }

  if (
    pathname === "/esplora-la-mappa" ||
    pathname.startsWith("/esplora-la-mappa/") ||
    pathname === "/mappa-globale"
  ) {
    const mode = (params.get("mode") || "").toLowerCase();
    const macro = (params.get("macro") || "").toLowerCase();
    if (mode === "mare") {
      return {
        title: "Mappa del Mare · 6amico",
        description:
          "Esplora spiagge, costa e rotte marine del network 6amico sulla mappa dinamica del mare.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    if (macro === "montagna") {
      return {
        title: "Mappa della Montagna · 6amico",
        description:
          "Sentieri, vette e percorsi di montagna del network 6amico sulla mappa dinamica.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    if (macro === "terra" || mode === "terra") {
      return {
        title: "Mappa della Terra · 6amico",
        description:
          "Colline, borghi e percorsi dell’entroterra del network 6amico sulla mappa dinamica.",
        url: canonical,
        image: DEFAULT_SEO_IMAGE,
      };
    }
    return {
      title: "Esplora la Mappa · 6amico",
      description:
        "Naviga Mare, Terra e Montagna sul network territoriale 6amico — una sola mappa, tre esperienze.",
      url: canonical,
      image: DEFAULT_SEO_IMAGE,
    };
  }

  return {
    title: DEFAULT_SEO_TITLE,
    description: DEFAULT_SEO_DESC,
    url: `${SITE_ORIGIN}${pathname === "/" ? "/" : pathname}`,
    image: DEFAULT_SEO_IMAGE,
  };
}

export function setActivePostSeoId(id) {
  activePostSeoId = id ?? null;
}

export function getActivePostSeoId() {
  return activePostSeoId;
}

export function clearPostSeoIf(postId) {
  if (activePostSeoId == null || String(activePostSeoId) !== String(postId)) return;
  activePostSeoId = null;
  if (typeof window === "undefined") return;
  applySeo(resolveLaunchSeo(window.location.pathname, window.location.search));
}
